import React, { useState, useEffect } from 'react';
import { FiDollarSign } from 'react-icons/fi';
import { formatNumber } from '../../../utils/formatters';
import { KPICard as UIKPICard, LoadingSpinner } from '../../ui';
import { useExecutiveTotalRevenue } from '../../../hooks/useExecutiveKPIs';

import { KPIProps } from '../../../types/kpi';
const DollarSignIcon = FiDollarSign as React.ElementType;

const TotalRevenueKPI: React.FC<KPIProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false,
  className = ''
}) => {
  const [animatedValue, setAnimatedValue] = useState<number>(0);
  
  // Fetch revenue data
  const { data, isLoading, error } = useExecutiveTotalRevenue(branchId, timeRange as string);
  
  // Extract data
  const revenue = data?.revenue || 0;
  const revenueGrowth = data?.revenueGrowth || 0;
  const currency = data?.currency || {};
  
  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);
  
  // Animation for numbers
  useEffect(() => {
    if (!revenue) return;
    
    const endValue = Math.floor(revenue);
    const startValue = animatedValue || 0;

    const animateValue = (start: number, end: number, duration: number) => {
      if (start === end) {
        setAnimatedValue(end);
        return;
      }
      
      let startTimestamp: number | null = null;
      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const value = Math.floor(progress * (end - start) + start);
        setAnimatedValue(value);
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    };

    const timerId = setTimeout(() => {
      animateValue(startValue, endValue, 1000);
    }, 100);
    
    return () => clearTimeout(timerId);
  }, [revenue]);
  
  if (isLoading && showLoading) {
    return (
      <div className={`animate-enter animate-delay-1 ${className}`}>
        <div className="card-base card-padding">
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner variant="minimal" size="md" />
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <UIKPICard
      title="درآمد کل"
      value={`${formatNumber(animatedValue)} ${currency.currency_symbol || "تومان"}`}
      subtitle={revenueGrowth !== 0 ? `${revenueGrowth > 0 ? '+' : ''}${revenueGrowth}%` : undefined}
      variant="primary"
      accentStyle="corner"
      className={`animate-enter ${className}`}
      icon={<DollarSignIcon size={20} />}
    />
  );
};

export default TotalRevenueKPI;
